import React from "react";
import {
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { clsx } from "clsx";
import { useNotifications } from "../stores/notificationStore";
import { Text } from "./Text";

// Constants for notification styling
const NOTIFICATION_STYLES = {
  success: "bg-emerald-500 text-white",
  error: "bg-red-500 text-white", 
  warning: "bg-yellow-500 text-white",
  info: "bg-black text-white",
} as const;

const ICON_SIZE = "w-5 h-5";
const CLOSE_ICON_SIZE = "w-4 h-4";

type NotificationType = keyof typeof NOTIFICATION_STYLES;

// Icon mapping function

// Style mapping function
const getNotificationStyles = (type: string) => {
  return NOTIFICATION_STYLES[type as NotificationType] || NOTIFICATION_STYLES.info;
};

export const NotificationContainer = () => {
  const { notifications, remove } = useNotifications();

  return (
    <div className="fixed top-4 right-4 z-100 space-y-2">
      {notifications.map((notification) => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          onRemove={() => remove(notification.id)}
        />
      ))}
    </div>
  );
};

// Separate component for individual notification item
interface NotificationItemProps {
  notification: {
    id: string;
    type: string;
    message: string;
  };
  onRemove: () => void;
}

const NotificationItem = ({ notification, onRemove }: NotificationItemProps) => {
  const containerClasses = clsx(
    "min-w-80 p-2 rounded-lg",
    "flex items-center gap-3 justify-center align-middle",
    "transition-all duration-300 transform",
    getNotificationStyles(notification.type)
  );

  const getNotificationIcon = (type: string) => {
  const iconClass = ICON_SIZE;
  
  switch (type) {
    case "success":
      return <CheckCircleIcon className={iconClass} />;
    case "error":
      return <XCircleIcon className={iconClass} />;
    case "warning":
      return <ExclamationTriangleIcon className={iconClass} />;
    default:
      return <InformationCircleIcon className={iconClass} />;
  }
};


  return (
    <div className={containerClasses}>
      {getNotificationIcon(notification.type)}
      <p className="flex-1 text-sm">
        {notification.message}
      </p>
      <button
        onClick={onRemove}
        className="hover:opacity-70 transition-opacity cursor-pointer"
        aria-label="Close notification"
      >
        <XMarkIcon className={CLOSE_ICON_SIZE} />
      </button>
    </div>
  );
};
